/**
 * receiptNormalizer.ts
 *
 * Colapsa los ~11 campos ext* de un IReceipt en 5 campos canónicos
 * que el motor de conciliación puede consumir directamente.
 *
 * Matriz de casos:
 *  platformType          extCashSourceType         paymentNature resultado
 *  ───────────────────   ───────────────────────   ────────────────────────
 *  WireTransfer          (n/a)                     BANK_TRANSFER
 *  Cash                  Cuenta bancaria           BANK_TRANSFER
 *  Cash                  Sucursal                  CASH_OFFICE
 *  Cash                  Caja de sucursal          CASH_OFFICE
 *  Cash                  POS físico                CARD_GATEWAY
 *  Cash                  Link de pago externo      CARD_GATEWAY
 *  Cash                  Crédito fiscal            FISCAL_ADJUSTMENT
 *  Cash                  (otros/null)              CASH_OFFICE
 *  Bancard               (n/a)                     CARD_GATEWAY
 *  SiemprePago           (n/a)                     CARD_GATEWAY
 *  Wompi                 (n/a)                     CARD_GATEWAY
 *  DLocal                (n/a)                     CARD_GATEWAY
 *  PaymentSimulator      (n/a)                     AIRLINE_CARD
 *  ItauPoints            (n/a)                     POINTS
 *  (otros)               (n/a)                     UNKNOWN
 */

import { IReceipt } from '../types';

// ─── Tipo canónico de naturaleza de pago ─────────────────────────────────────

export type PaymentNature =
	| 'BANK_TRANSFER' // Transferencia bancaria (wire o depósito)
	| 'CARD_GATEWAY' // Tarjeta cobrada vía gateway (Bancard, Wompi, DLocal, POS, link)
	| 'AIRLINE_CARD' // Tarjeta cobrada directamente por aerolínea (PaymentSimulator)
	| 'CASH_OFFICE' // Efectivo o cobro en oficina/sucursal
	| 'POINTS' // Puntos (ItauPoints)
	| 'FISCAL_ADJUSTMENT' // Ajuste contable (crédito fiscal, pérdida TC, wallet/crypto)
	| 'UNKNOWN';

export const PAYMENT_NATURE_LABELS: Record<PaymentNature, string> = {
	BANK_TRANSFER: 'Transferencias bancarias',
	CARD_GATEWAY: 'Tarjetas / Gateways',
	AIRLINE_CARD: 'Tarjeta contra Aerolínea',
	CASH_OFFICE: 'Cajas de oficina',
	POINTS: 'Puntos',
	FISCAL_ADJUSTMENT: 'Ajustes contables',
	UNKNOWN: 'Sin clasificar',
};

// Sucursales que Power registra como "Cash" pero son wallets/crypto/ajustes contables
const NON_BANK_BRANCH_PATTERNS = [
	/usdt/i,
	/mercadopago/i,
	/pago/i,
	/p[eé]rdida/i,
	/crypto/i,
	/wallet/i,
	/canje/i,
	/retenci[oó]n/i,
];

// ─── Función principal de derivación de naturaleza ────────────────────────────

export function derivePaymentNature(
	platformType: string,
	extCashSourceType: string | null | undefined,
	extBranchName: string | null | undefined,
): PaymentNature {
	const pt = (platformType ?? '').toLowerCase().replace(/\s/g, '');
	const cst = (extCashSourceType ?? '').toLowerCase();

	switch (pt) {
		case 'wiretransfer':
			return 'BANK_TRANSFER';

		case 'cash': {
			// Detectar wallets/crypto/ajustes por extBranchName antes del subtipo
			if (extBranchName) {
				for (const pat of NON_BANK_BRANCH_PATTERNS) {
					if (pat.test(extBranchName)) return 'FISCAL_ADJUSTMENT';
				}
			}
			if (cst.includes('cuenta bancaria')) return 'BANK_TRANSFER';
			if (cst.includes('sucursal') || cst.includes('caja')) return 'CASH_OFFICE';
			if (cst.includes('pos') || cst.includes('link de pago') || cst.includes('link de stripe')) return 'CARD_GATEWAY';
			if (cst.includes('crédito fiscal') || cst.includes('credito fiscal')) return 'FISCAL_ADJUSTMENT';
			// fallback: si no hay subtipo o es desconocido, tratamos como caja
			return 'CASH_OFFICE';
		}

		case 'bancard':
		case 'siemprepago':
		case 'wompi':
		case 'dlocal':
			return 'CARD_GATEWAY';

		case 'paymentsimulator':
			return 'AIRLINE_CARD';

		case 'itaupoints':
			return 'POINTS';

		default:
			if (/wire/i.test(platformType)) return 'BANK_TRANSFER';
			if (/simulator/i.test(platformType)) return 'AIRLINE_CARD';
			if (/points/i.test(platformType)) return 'POINTS';
			if (/bancard|wompi|dlocal|siempre/i.test(platformType)) return 'CARD_GATEWAY';
			return 'UNKNOWN';
	}
}

// ─── Resultado normalizado de un recibo ──────────────────────────────────────

export interface NormalizedReceipt {
	id: string;
	nature: PaymentNature;
	/** Banco o gateway canónico (ej: "BROU", "DLOCAL") */
	entity: string;
	/** Valor crudo original del campo de entidad, para auditoría */
	entityRaw: string;
	/** Solo para CASH_OFFICE: la sucursal física */
	location: string;
	/** Mejor ID disponible: extGatewayTx ?? extPaymentReference ?? extAuthCode */
	externalRef: string;
	/** "VISA ····1234" cuando aplica */
	cardInfo: string;
	amountUSD: number;
	createdAt: string;
}

function normalizeEntityName(raw: string): string {
	const r = (raw ?? '').toLowerCase();
	if (r.includes('brou')) return 'BROU';
	if (r.includes('itaú') || r.includes('itau')) return 'ITAÚ';
	if (r.includes('santander')) return 'SANTANDER';
	if (r.includes('bbva')) return 'BBVA';
	if (r.includes('relay')) return 'RELAY';
	if (r.includes('bancard')) return 'BANCARD';
	if (r.includes('dlocal')) return 'DLOCAL';
	if (r.includes('wompi')) return 'WOMPI';
	if (r.includes('siemprepago') || r.includes('siempre pago')) return 'SIEMPREPAGO';
	return raw?.trim() || '—';
}

export function normalizeReceipt(receipt: IReceipt): NormalizedReceipt {
	const nature = derivePaymentNature(receipt.platformType, receipt.extCashSourceType, receipt.extBranchName);

	let entityRaw = '';
	let entity = '';
	let location = '';

	switch (nature) {
		case 'BANK_TRANSFER':
			// Wire usa extBankName; Cash+CuentaBancaria usa extBranchName
			entityRaw =
				receipt.platformType === 'WireTransfer'
					? receipt.extBankName ?? receipt.extBranchName ?? ''
					: receipt.extBranchName ?? receipt.extBankName ?? '';
			entity = normalizeEntityName(entityRaw);
			break;

		case 'CARD_GATEWAY':
			entityRaw = ['Bancard', 'DLocal', 'Wompi', 'SiemprePago'].includes(receipt.platformType)
				? receipt.platformType
				: receipt.extBranchName ?? receipt.platformType ?? '';
			entity = normalizeEntityName(entityRaw);
			break;

		case 'AIRLINE_CARD':
			entityRaw = receipt.extAirlineName ?? receipt.platformType ?? '';
			entity = entityRaw.trim() || 'AEROLÍNEA';
			break;

		case 'CASH_OFFICE':
			entityRaw = receipt.extBranchName ?? '';
			entity = entityRaw.trim() || 'CAJA';
			location = entityRaw;
			break;

		case 'POINTS':
			entityRaw = 'ItauPoints';
			entity = 'ITAÚ POINTS';
			break;

		case 'FISCAL_ADJUSTMENT':
			entityRaw = receipt.extBranchName ?? receipt.extCashSourceType ?? '';
			entity = entityRaw.trim() || 'AJUSTE';
			break;

		default:
			entityRaw = receipt.extBranchName ?? receipt.platformType ?? '';
			entity = entityRaw || 'DESCONOCIDO';
	}

	return {
		id: receipt.id,
		nature,
		entity,
		entityRaw,
		location,
		externalRef: receipt.extGatewayTransactionId ?? receipt.extPaymentReference ?? receipt.extAuthCode ?? '',
		cardInfo:
			receipt.extCardStamp && receipt.extCardLast4
				? `${receipt.extCardStamp} ····${receipt.extCardLast4}`
				: receipt.extCardStamp ?? '',
		amountUSD: receipt.amountInMainCurrency,
		createdAt: receipt.createdAt,
	};
}
